<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    <div class="px-6 pt-6 pb-6">

      <Loader :visible="isLoading" :text="loadingText" />

      <div class="bg-white rounded-lg shadow-lg p-4">
        <!-- Filtro cliente sopra il calendario -->
        <div class="mb-4 flex items-center justify-between">
          <div class="flex items-center gap-4">
            <ClientSelectorModal
              :clients-list="clientsList"
              :selected-client="selectedClient"
              @client-selected="selectClient"
            />
            
            <!-- Checkbox per nascondere appuntamenti passati -->
            <label class="flex items-center text-sm text-gray-700">
              <input 
                type="checkbox" 
                v-model="hidePastAppointments" 
                class="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              Nascondi appuntamenti passati
            </label>
          </div>
          
          <div>
            <button 
              @click="goToCalendario"
              class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Calendario
            </button>
          </div>
        </div>
        
        <!-- Mappa Google Maps -->
        <div id="map-container" style="height: 600px; width: 100%;">
          <GoogleMap
            ref="googleMap"
            :api-key="googleMapsApiKey"
            style="width: 100%; height: 100%;"
            :center="mapCenter"
            :zoom="15"
            :map-type-control="false"
            :street-view-control="false"
            @bounds_changed="handleBoundsChanged"
            @zoom_changed="handleZoomChanged"
            @dragend="handleMapDrag"
          >
            <!-- Pin per ogni appuntamento -->
            <Marker
              v-for="address in visitsData"
              :key="address.id"
              :options="{ 
                position: { lat: parseFloat(address.latitude), lng: parseFloat(address.longitude)}, 
                title: `${address.company_name}`,
                zIndex: 100
              }"
            >
              <InfoWindow>
                <div class="p-2 max-w-xs">
                  <h4 class="font-bold text-sm">{{ address.company_name }}</h4>
                  <p class="text-xs">{{ address.address }}</p>
                </div>
                <div class="p-2 max-w-xs">
                  <h4 class="font-bold text-sm">Visite programmate:</h4>
                  <p style="padding: 5px 0px" v-for="visit in address.visits" :key="visit.id">
                    <strong>
                      <span v-if="visit.operators && visit.operators.length > 0">
                        {{ visit.operators.map(op => op.name).join(', ') }}
                      </span>
                      <span v-else>
                        {{ visit.creator?.name || 'N/A' }}
                      </span>
                    </strong><br />
                    {{ formatDateTime(visit.start_time) }}<br />
                    {{ visit.description }}<br />
                    <button 
                      @click.prevent="handleMarkerClick(visit)"
                      class="mt-2 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                    >
                      <span v-if="$page.props.isAdminOrSuperAdmin || visit.creator.id === $page.props.auth.user.id || visit.operators.find(x => x.id == $page.props.auth.user.id)">Modifica appuntamento</span>
                      <span v-else>Visualizza appuntamento</span>
                    </button>
                  </p>
                </div>
              </InfoWindow>
            </Marker>

            <!-- Pin per i clienti nella mappa -->
            <Marker
              v-for="client in clientsInBounds"
              :key="`client-${client.id}`"
              :options="{ 
                position: { lat: parseFloat(client.Latitude), lng: parseFloat(client.Longitude)}, 
                title: client.company_name,
                icon: {
                  url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                },
                zIndex: 50
              }"
            >
              <InfoWindow>
                <div class="p-2 max-w-xs">
                  <h4 class="font-bold text-sm">{{ client.CompanyName }}</h4>
                  <p class="text-xs">{{ client.Address }}</p>
                  <button 
                    @click="createAppointmentForClient(client)"
                    class="mt-2 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                  >
                    Crea Appuntamento
                  </button>
                </div>
              </InfoWindow>
            </Marker>
          </GoogleMap>
        </div>
      </div>
    </div>

    <!-- Modal nuovo appuntamento -->
    <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative my-auto">
        <h3 class="text-lg font-semibold mb-4">
          {{ editingVisit ? 'Modifica Appuntamento' : 'Nuovo Appuntamento' }}
        </h3>
        <form @submit.prevent="editingVisit ? updateAppointment() : saveAppointment()" class="max-h-[70vh] overflow-y-auto">
          <!-- Toggle per scegliere tra Cliente+Sede e Contatto -->
          <div class="mb-4">
            <label class="block text-gray-700 mb-2">Seleziona indirizzo da:</label>
            <div class="flex space-x-4">
              <label class="flex items-center">
                <input type="radio" v-model="addressSourceType" value="cliente" class="mr-2" />
                Cliente
              </label>
              <label class="flex items-center">
                <input type="radio" v-model="addressSourceType" value="contatto" class="mr-2" />
                Contatto
              </label>
            </div>
          </div>
          
          <!-- Selezione Cliente (visibile solo se addressSourceType è 'cliente') -->
          <div v-if="addressSourceType === 'cliente'" class="mb-4">
            <label class="block text-gray-700">Cliente</label>
            <v-select :options="foundClients" :filterable="false" v-model="newAppointment.company" label="name"
              @search="searchClients" @option:selected="onClientSelect" required>
              <template #no-options="{ search, searching }">
                <div v-if="searching">🔍 Ricerca in corso...</div>
                <div v-else-if="!search">Inizia a digitare per cercare un cliente...</div>
              </template>
            </v-select>
            <div v-if="newAppointment.errors.company" class="py-4 text-red-800">{{ newAppointment.errors.company }}</div>
          </div>
          
          <!-- Selezione Sede (visibile solo se addressSourceType è 'cliente') -->
          <div v-if="addressSourceType === 'cliente'" class="mb-4">
            <label class="block text-gray-700">Sede</label>
            <select v-model="newAppointment.branch_id" class="p93__input" :disabled="!newAppointment.company" @change="setAddressFromSite">
              <option value="" :selected="newAppointment.branch_id === ''">Sede principale</option>
              <option v-for="site in sites" :key="site.id" :value="site.id">{{ site.name }}</option>
            </select>
            <div v-if="newAppointment.errors.branch_id" class="py-4 text-red-800">{{ newAppointment.errors.branch_id }}</div>
          </div>
          
          <!-- Selezione Contatto (visibile solo se addressSourceType è 'contatto') -->
          <div v-if="addressSourceType === 'contatto'" class="mb-4">
            <label class="block text-gray-700">Contatto</label>
            <v-select :options="foundContacts" :filterable="false" v-model="newAppointment.contact" label="name"
              @search="searchContacts" @option:selected="onContactSelect" required>
              <template #no-options="{ search, searching }">
                <div v-if="searching">🔍 Ricerca in corso...</div>
                <div v-else-if="!search">Inizia a digitare per cercare un contatto...</div>
              </template>
            </v-select>
            <div v-if="newAppointment.errors.contact" class="py-4 text-red-800">{{ newAppointment.errors.contact }}</div>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Indirizzo</label>
            <input
              v-model="newAppointment.address"
              type="text"
              class="p93__input"
              placeholder="Indirizzo selezionato automaticamente"
              readonly
            />
            <div v-if="newAppointment.errors.address" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.address }}</div>
          </div>
          
          <!-- Campo selezione operatori se l'utente è admin/super admin -->
          <div v-if="users && users.length > 0" class="mb-4">
            <label class="block text-sm font-medium mb-2">Operatori assegnati</label>
            <div class="border border-gray-300 rounded-md p-3 bg-gray-50 max-h-40 overflow-y-auto">
              <div v-for="user in users" :key="user.id" class="flex items-center mb-2 last:mb-0">
                <input 
                  type="checkbox" 
                  :id="`operator-${user.id}`"
                  :value="user.id"
                  v-model="newAppointment.operator_ids"
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label 
                  :for="`operator-${user.id}`" 
                  class="ml-2 text-sm text-gray-700 cursor-pointer select-none"
                >
                  {{ user.name }}
                </label>
              </div>
              <div v-if="users.length === 0" class="text-gray-500 text-sm">
                Nessun operatore disponibile
              </div>
            </div>
            <small class="text-gray-600 mt-1 block">Seleziona uno o più operatori per questo appuntamento</small>
            <div v-if="newAppointment.errors.operator_ids" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.operator_ids }}</div>
          </div>
          
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Data</label>
            <input
              type="date"
              v-model="newAppointment.date"
              class="p93__input"
              required
            />
            <div v-if="newAppointment.errors.date" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.date }}</div>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-sm font-medium mb-1">Ora Inizio</label>
              <input
                type="time"
                v-model="newAppointment.start_time"
                class="p93__input"
                required
              />
              <div v-if="newAppointment.errors.start_time" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.start_time }}</div>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">Ora Fine</label>
              <input
                type="time"
                v-model="newAppointment.end_time"
                class="p93__input"
                required
              />
              <div v-if="newAppointment.errors.end_time" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.end_time }}</div>
            </div>
          </div>
          <div class="mb-6">
            <label class="block text-sm font-medium mb-1">Motivazione</label>
            <textarea
              v-model="newAppointment.description"
              class="p93__input"
              rows="2"
              placeholder="Motivazione della visita"
              required
            ></textarea>
          </div>
          
          <!-- Campo note di conclusione per visite già concluse -->
          <div v-if="editingVisit && editingVisit.concluded_at" class="mb-6">
            <label class="block text-sm font-medium mb-1">
              Note di conclusione 
              <span class="text-green-600 text-sm">
                (conclusa il {{ new Date(editingVisit.concluded_at).toLocaleString('it-IT') }})
              </span>
            </label>
            <textarea
              v-model="newAppointment.conclusion_notes"
              class="p93__input"
              rows="3"
              placeholder="Note sulla conclusione della visita..."
            ></textarea>
          </div>
          
          <!-- Campo note di conclusione per nuova conclusione -->
          <div v-if="showConcludeConfirmation" class="mb-6">
            <label class="block text-sm font-medium mb-1">Note di conclusione (opzionale)</label>
            <textarea
              v-model="conclusionNotes"
              class="p93__input"
              rows="3"
              placeholder="Inserisci eventuali note sulla conclusione della visita..."
            ></textarea>
          </div>

          <div v-if="editingVisit && uploadDMS && editingVisit.concluded_at">
            <label class="block text-sm font-medium mb-1">Allegati caricati</label>
            <div v-if="!attachmentLoaded">
              <span>Caricamento allegati...</span>
            </div>
            <div v-else>
              <ul class="list-disc list-inside mb-4">
                <li v-for="attachment in visitAttachments" :key="attachment.id" class="text-sm text-gray-700">
                  <a :href="route('modules.visite-commerciali.allegatoDMS', attachment.id)" target="_blank" class="text-blue-600 hover:underline">
                    {{ attachment.name }}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="showConcludeConfirmation && uploadDMS" class="mb-6">
            <div
                    class="border-2 border-dashed rounded-2xl p-4 relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 hover:border-gray-400"
                    role="button"
                    tabindex="0"
                    @click="openFileDialog"
                    @keydown.enter.prevent="openFileDialog"
                    @keydown.space.prevent="openFileDialog"
                    @dragenter.prevent="onDragEnter"
                    @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDrop"
                    aria-describedby="filepicker-desc"
                  >
                    <input
                      ref="input"
                      type="file"
                      class="sr-only"
                      @change="onInputChange"
                      multiple="true"
                      aria-hidden="true"
                    />

                    <div class="flex items-center gap-3">
                      <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M12 3v12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8 7l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M21 21H3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>

                      <div>
                        <p id="filepicker-desc" class="text-sm text-gray-700">Trascina qui i file che vuoi allegare oppure fai click per selezionarli.</p>
                      </div>
                    </div>

                    <div v-if="currentAttachments.length" class="mt-3 space-y-2">
                      <div v-for="f in currentAttachments" :key="f.name" class="flex items-center justify-between gap-2">
                        <div class="flex items-center gap-2">
                          <svg class="w-5 h-5 text-gray-500" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M4 7v8a2 2 0 0 0 2 2h8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M10 3v6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          <div>
                            <div class="text-sm font-medium text-gray-800">{{ f.name }}</div>
                            <div class="text-xs text-gray-500">{{ prettySize(f.size) }}</div>
                          </div>
                        </div>

                        <div class="flex items-center gap-2">
                          <button type="button" class="text-sm px-3 py-1 rounded-md border" @click.stop="removeFile(f)">Rimuovi</button>
                        </div>
                      </div>
                    </div>
                  </div>

          </div>
          
          <div class="flex justify-end gap-2">
            <template v-if="showDeleteConfirmation">
              <button type="button" @click="cancelDelete" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Annulla Eliminazione
              </button>
              <button type="button" @click="confirmDelete" class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                Conferma Eliminazione
              </button>
            </template>
            <template v-else-if="showConcludeConfirmation">
              <button type="button" @click="cancelConclude" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Annulla
              </button>
              <button type="button" @click="confirmConclude" class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                Conferma Conclusione
              </button>
            </template>
            <template v-else>
              <button type="button" @click="closeModal" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Annulla</button>
              <button 
                v-if="editingVisit && !editingVisit.concluded_at && ($page.props.isAdminOrSuperAdmin || this.editingVisit.user.id === $page.props.auth.user.id || this.editingVisit.operators.find(x => x.id == $page.props.auth.user.id))"
                type="button" 
                @click="showConcludeConfirmation = true" 
                class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Concludi
              </button>
              <button 
                v-if="editingVisit && ($page.props.isAdminOrSuperAdmin || this.editingVisit.user.id === $page.props.auth.user.id)"
                type="button" 
                @click="showDeleteConfirmation = true" 
                class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Elimina
              </button>
              <button
                v-if="(editingVisit && ($page.props.isAdminOrSuperAdmin || this.editingVisit.user.id === $page.props.auth.user.id || this.editingVisit.operators.find(x => x.id == $page.props.auth.user.id))) || !editingVisit"
                type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                {{ editingVisit ? 'Aggiorna' : 'Salva' }}
              </button>
            </template>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, useForm, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import ClientSelectorModal from '@/Components/ClientSelectorModal.vue';
import { GoogleMap, Marker, InfoWindow } from 'vue3-google-map';
import Loader from '@/Components/Loader.vue';

export default {
  props: ['errors', 'clientsList', 'users', 'uploadDMS'],
  components: {
    Link,
    BreadCrumbs,
    ClientSelectorModal,
    GoogleMap,
    Marker,
    InfoWindow,
    Loader
  },
  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Visite commerciali', href: route('modules.home', 'visite-commerciali') },
        { name: 'Mappa', href: route('modules.visite-commerciali.mappa') }
      ],
      showModal: false,
      showClientSuggestions: false,
      clientSearchTerm: '',
      selectedClient: '',
      hidePastAppointments: true, // Nuovo flag per nascondere appuntamenti passati
      selectedDate: null,
      autocomplete: null,
      editingVisit: null,
      showDeleteConfirmation: false,
      showConcludeConfirmation: false,
      conclusionNotes: '',
      showAddressPicker: false,
      visitsData: [], // Memorizza localmente le visite caricate
      clientsInBounds: [], // Nuova proprietà per i clienti nella mappa
      mapCenter: { lat: 45.0732, lng: 7.6827 }, // Centro del Piemonte (Torino)
      selectedMapPosition: null, // Per memorizzare la posizione cliccata sulla mappa
      isLoadingVisits: false,
      isLoadingClients: false, // Nuovo flag per il caricamento clienti
      boundsChangeTimeout: null, // Per il debouncing
      isClientAppointment: false, // Flag per indicare se l'appuntamento è creato da un pin cliente
      newAppointment: useForm({
        address: '',
        latitude: null,
        longitude: null,
        start_time: '',
        end_time: '',
        description: '',
        date: '',
        company: '',
        company_name: '',
        operator_ids: [], // Nuovo campo per gli operatori assegnati
        source: 'mappa',
        branch_id: '',
        contact: '', // Nuovo campo per il contatto selezionato
        contact_id: '', // ID del contatto selezionato
        conclusion_notes: '' // Campo per le note di conclusione
      }),
      foundClients: [],
      foundContacts: [], // Nuova proprietà per i contatti trovati
      sites: [],
      addressSourceType: 'cliente', // Determina se selezionare sede cliente o contatto
      currentAttachments: [],
      readfileramaining: -1,
      isLoading: false,
      loadingText: 'Caricamento...',
      visitAttachments: [],
      attachmentLoaded: false
    };
  },
  mounted() {
    // Aggiungi un ritardo per assicurarsi che Google Maps sia caricato
    setTimeout(() => {
      this.loadVisits();
    }, 500);
    console.log(this.users);
  },
  beforeUnmount() {
    // Cleanup non più necessario per place picker
  },
  watch: {
    selectedClient() {
      // Ricarica automaticamente la mappa quando cambia il filtro
      this.loadVisits();
    },
    hidePastAppointments() {
      // Ricarica automaticamente quando cambia il filtro appuntamenti passati
      this.loadVisits();
    },
    addressSourceType() {
      // Reset dei campi quando cambia il tipo di selezione indirizzo
      if (this.editingVisit) return;
      this.resetAddressSelection();
    }
  },
  methods: {
    async handleMapClick(event) {
      // Gestisce il click sulla mappa per creare un nuovo appuntamento
      this.selectedMapPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      
      // Reset del flag cliente
      this.isClientAppointment = false;
      
      // Usa il servizio di geocoding del mixin per ottenere l'indirizzo
      const address = await this.getAddressFromCoordinates(this.selectedMapPosition.lat, this.selectedMapPosition.lng);
      this.newAppointment.address = address;
      
      // Imposta data e ora predefinite
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const currentHour = now.getHours();
      const nextHour = currentHour + 1;
      
      this.newAppointment.date = today;
      this.newAppointment.start_time = `${currentHour.toString().padStart(2, '0')}:00`;
      this.newAppointment.end_time = `${nextHour.toString().padStart(2, '0')}:00`;
      this.newAppointment.latitude = this.selectedMapPosition.lat;
      this.newAppointment.longitude = this.selectedMapPosition.lng;
      
      this.showModal = true;
    },
    
    handleMarkerClick(visit) {
      // Gestisce il click su un marker per modificare l'appuntamento
      this.editingVisit = visit;
      this.showAddressPicker = false;
      
      // Precompila il form con i dati della visita esistente
      this.newAppointment.address = visit.address || '';
      this.newAppointment.company = visit.company || '';
      this.newAppointment.description = visit.description || '';
      this.newAppointment.latitude = visit.latitude;
      this.newAppointment.longitude = visit.longitude;
      this.newAppointment.branch_id = visit.branch_id || '';
      this.newAppointment.company_name = visit.company?.name || '';
      this.newAppointment.contact = visit.contact || '';
      this.newAppointment.contact_id = visit.contact ? visit.contact.id : '';
      this.newAppointment.conclusion_notes = visit.conclusion_notes || '';
      
      // Precompila gli operatori assegnati se presenti
      this.newAppointment.operator_ids = visit.operators ? visit.operators.map(op => op.id) : [];
      
      // Estrai data e orari dal datetime
      const startDate = new Date(visit.start_time);
      const endDate = new Date(visit.end_time);
      
      this.newAppointment.date = startDate.toISOString().split('T')[0];
      this.newAppointment.start_time = startDate.toTimeString().slice(0, 5);
      this.newAppointment.end_time = endDate.toTimeString().slice(0, 5);

      // Determina il tipo di selezione indirizzo basato sui dati esistenti
      if (visit.company_name) {
        this.addressSourceType = 'cliente';
        // Precarica il cliente nell'array foundClients per farlo apparire nella select
        if (visit.company) {
          this.foundClients = [visit.company];
          this.onClientSelect(visit.company, false);
        }
        if (visit.branch_id) {
          this.newAppointment.branch_id = visit.branch_id;
          this.setAddressFromSite();
        }
      } else if (visit.contact && visit.contact.name) {
        this.addressSourceType = 'contatto';
        // Precarica il contatto nell'array foundContacts per farlo apparire nella select
        this.foundContacts = [visit.contact];
        this.newAppointment.contact = visit.contact;
        this.onContactSelect(visit.contact, false);
      }
      
      this.showModal = true;

      if (this.uploadDMS){
        this.attachmentLoaded = false;
        this.loadAttachments();
      }

      debugger;
    },
    
    handleBoundsChanged() {
      // Debouncing per evitare troppe chiamate API
      if (this.boundsChangeTimeout) {
        clearTimeout(this.boundsChangeTimeout);
      }
      
      this.boundsChangeTimeout = setTimeout(() => {
        this.loadClientsInBounds();
      }, 500); // Attendi 1 secondo dopo che l'utente smette di muovere la mappa
    },
    
    handleZoomChanged() {
      this.handleBoundsChanged();
    },
    
    handleMapDrag() {
      this.handleBoundsChanged();
    },

    loadClientsInBounds() {
      if (!this.$refs.googleMap || !this.$refs.googleMap.map) {
        return;
      }
      
      const map = this.$refs.googleMap.map;
      const bounds = map.getBounds();
      
      if (!bounds) {
        return;
      }
      
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      
      this.isLoadingClients = true;
      
      const params = {
        north: ne.lat(),
        south: sw.lat(),
        east: ne.lng(),
        west: sw.lng(),
      };

      // Chiamata API per ottenere i clienti nell'area visibile
      axios.get(route('modules.visite-commerciali.clienti-mappa'), { params })
        .then(response => {
          this.clientsInBounds = response.data.data || response.data;
          this.isLoadingClients = false;
        })
        .catch(error => {
          console.error('Errore nel caricamento dei clienti:', error);
          this.isLoadingClients = false;

          // Gestisci l'errore silenziosamente o mostra un messaggio discreto
          if (error.response && error.response.status !== 404) {
            this.$toast?.error?.('Errore nel caricamento dei clienti') || 
            console.warn('Errore nel caricamento dei clienti');
          }
        });
    },

    createAppointmentForClient(client) {
      this.newAppointment.address = client.Address + ' ' + (client.ZIPCode || '') + ' ' + (client.City || '') + ' ' + (client.County || '');
      this.newAppointment.company = {
        id: client.CustSupp,
        name: client.CompanyName,
        Address: client.Address,
        City: client.City,
        ZIPCode: client.ZIPCode,
        County: client.County,
        Latitude: client.Latitude,
        Longitude: client.Longitude
      };
      this.newAppointment.company_name = client.CompanyName;
      this.newAppointment.latitude = parseFloat(client.Latitude);
      this.newAppointment.longitude = parseFloat(client.Longitude);

      // Imposta data e ora predefinite
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const currentHour = now.getHours();
      const nextHour = currentHour + 1;
      
      this.newAppointment.date = today;
      this.newAppointment.start_time = `${currentHour.toString().padStart(2, '0')}:00`;
      this.newAppointment.end_time = `${nextHour.toString().padStart(2, '0')}:00`;
      
      this.editingVisit = null;
      this.showAddressPicker = false;
      this.isClientAppointment = true; // Imposta il flag per rendere i campi readonly
      
      // Carica le sedi del cliente
      this.getSites(client.CustSupp);
      
      this.showModal = true;
    },
    
    loadVisits() {
      // Carica le visite per la mappa
      this.isLoadingVisits = true;
      
      const params = {};

      // Condizionalmente aggiungi il filtro per le date future
      if (this.hidePastAppointments) {
        params.start = new Date().toISOString().split('T')[0];
      }
      params.groupByAddress = true;
      
      if (this.selectedClient) {
        params.company = this.selectedClient;
      }
      
      axios.get(route('modules.visite-commerciali.visite'), { params })
        .then(response => {
          const allVisits = response.data.data || response.data;
          this.visitsData = allVisits.filter(visit => {
            const hasCoords = visit.latitude && visit.longitude && 
                             !isNaN(parseFloat(visit.latitude)) && 
                             !isNaN(parseFloat(visit.longitude));
            
            return hasCoords;
          });
          
          // Centra la mappa se ci sono visite, altrimenti mantieni il centro del Piemonte
          if (this.visitsData.length > 0) {
            this.centerMapOnVisits();
          }
          
          this.isLoadingVisits = false;
          
          // Carica anche i clienti dopo aver caricato le visite
          this.$nextTick(() => {
            this.loadClientsInBounds();
          });
        })
        .catch(error => {
          console.error('Errore nel caricamento delle visite:', error);
          this.isLoadingVisits = false;
          
          // Mostra un messaggio di errore all'utente
          this.$toast?.error?.('Errore nel caricamento delle visite') || 
          alert('Errore nel caricamento delle visite');
        });
    },
    
    centerMapOnVisits() {
      try {
        // Se c'è una sola visita, centra su quella con zoom 15
        if (this.visitsData.length === 1) {
          const visit = this.visitsData[0];
          this.mapCenter = {
            lat: parseFloat(visit.latitude),
            lng: parseFloat(visit.longitude)
          };
          // Per una sola visita manteniamo il zoom attuale
          if (this.$refs.googleMap && this.$refs.googleMap.map) {
            this.$refs.googleMap.map.setZoom(15);
          }
        } else {
          // Calcola i bounds per includere tutte le visite
          let minLat = Infinity, maxLat = -Infinity;
          let minLng = Infinity, maxLng = -Infinity;
          
          this.visitsData.forEach(visit => {
            const lat = parseFloat(visit.latitude);
            const lng = parseFloat(visit.longitude);
            
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
          });
          
          // Centro della bounding box
          this.mapCenter = {
            lat: (minLat + maxLat) / 2,
            lng: (minLng + maxLng) / 2
          };
          
          // Usa i bounds per far vedere tutti i marker
          this.$nextTick(() => {
            if (this.$refs.googleMap && this.$refs.googleMap.map) {
              const bounds = new google.maps.LatLngBounds();
              
              this.visitsData.forEach(visit => {
                bounds.extend({
                  lat: parseFloat(visit.latitude),
                  lng: parseFloat(visit.longitude)
                });
              });
              
              // Fit della mappa ai bounds con padding
              this.$refs.googleMap.map.fitBounds(bounds, {
                top: 50,
                right: 50,
                bottom: 50,
                left: 50
              });
            }
          });
        }
      } catch (error) {
        console.error('Errore nel centrare la mappa:', error);
      }
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    resetForm() {
      this.newAppointment.clearErrors();
      this.newAppointment.reset();
      this.selectedDate = null;
      this.editingVisit = null;
      this.showAddressPicker = false;
      this.showClientSuggestions = false;
      this.showDeleteConfirmation = false;
      this.showConcludeConfirmation = false;
      this.conclusionNotes = '';
      this.isClientAppointment = false; // Reset del flag cliente
      this.foundClients = [];
      this.foundContacts = [];
      this.sites = [];
      this.addressSourceType = 'sede'; // Reset al valore predefinito
      this.visitAttachments = [];
      this.currentAttachments = [];
      this.attachmentLoaded = false;
    },
    resetAddressSelection() {
      // Reset dei campi quando cambia il tipo di selezione indirizzo
      this.newAppointment.address = '';
      this.newAppointment.latitude = null;
      this.newAppointment.longitude = null;
      this.newAppointment.company = '';
      this.newAppointment.company_name = '';
      this.newAppointment.branch_id = '';
      this.newAppointment.contact = '';
      this.newAppointment.contact_id = '';
      this.sites = [];
      this.foundClients = [];
      this.foundContacts = [];
    },
    saveAppointment() {
      this.newAppointment.post(route('modules.visite-commerciali.nuova'), {
        preserveScroll: true,
        onSuccess: () => {
          this.closeModal();
          this.resetForm();
          this.loadVisits();
          // Ricarica anche i clienti per aggiornare il filtro
          this.$nextTick(() => {
            this.loadClientsInBounds();
          });
        }
      });
    },
    updateAppointment() {
      this.newAppointment.post(route('modules.visite-commerciali.modifica', this.editingVisit.id), {
        preserveScroll: true,
        onSuccess: () => {
          this.closeModal();
          this.loadVisits();
          // Ricarica anche i clienti per aggiornare il filtro
          this.$nextTick(() => {
            this.loadClientsInBounds();
          });
        }
      });
    },
    deleteAppointment() {
      this.newAppointment.post(route('modules.visite-commerciali.elimina', this.editingVisit.id), {
        preserveScroll: true,
        onSuccess: () => {
          this.closeModal();
          this.loadVisits();
          // Ricarica anche i clienti per aggiornare il filtro
          this.$nextTick(() => {
            this.loadClientsInBounds();
          });
        }
      });
    },
    
    confirmDelete() {
      this.deleteAppointment();
    },
    
    cancelDelete() {
      this.showDeleteConfirmation = false;
    },
    showLoader(text = 'Caricamento...') {
      this.isLoading = true;
      this.loadingText = text;
    },
    hideLoader() {
      this.isLoading = false;
    },
    concludeAppointment(attachments = []) {
      this.showLoader('Registrazione in corso...');
      // Invio la richiesta per concludere la visita
      this.$inertia.post(route('modules.visite-commerciali.concludi', this.editingVisit.id), {
        conclusion_notes: this.conclusionNotes,
        source: 'mappa',
        attachments: attachments
      }, {
        preserveScroll: true,
        onSuccess: () => {
          this.currentAttachments = [];
          this.closeModal();
          this.loadVisits();
          // Ricarica anche i clienti per aggiornare il filtro
          this.$nextTick(() => {
            this.loadClientsInBounds();
          });
        },
        onFinish: () => {
          this.hideLoader();
        }
      });
    },
    
    confirmConclude() {
      if (this.currentAttachments.length > 0) {
        this.emitFiles();
      }
      else this.concludeAppointment();
    },
    
    cancelConclude() {
      this.showConcludeConfirmation = false;
      this.conclusionNotes = '';
    },
    
    closeClientModal() {
      this.showClientModal = false;
      this.clientSearchTerm = '';
    },
    
    selectClient(clientCompany) {
      this.selectedClient = clientCompany;
    },
    goToCalendario() {
      router.visit(route('modules.visite-commerciali.calendario'));
    },
    searchClients(search, loading) {
      if (search.length >= 2) {
        loading(true);
        clearTimeout(this.searchContactsTimeout);
        this.searchContactsTimeout = setTimeout(() => {
          axios
            .get(route('modules.contratti.ricerca-clienti', search))
            .then((response) => {
              this.foundClients = response.data;
              loading(false);
            });
        }, 300);
      }
    },
    searchContacts(search, loading) {
      if (search.length >= 2) {
        loading(true);
        clearTimeout(this.searchContactsTimeout);
        this.searchContactsTimeout = setTimeout(() => {
          axios
            .get(route('modules.visite-commerciali.ricerca-contatti', search))
            .then((response) => {
              this.foundContacts = response.data;
              loading(false);
            });
        }, 300);
      }
    },
    onClientSelect(selectedClient) {
      this.newAppointment.company_name = selectedClient.name || '';
      this.getSites(selectedClient);
      this.setAddressFromClient(selectedClient);
    },
    onContactSelect(selectedContact) {
      this.newAppointment.contact_id = selectedContact.id;
      this.newAppointment.company_name = selectedContact.name || '';
      this.setAddressFromContact(selectedContact);
    },
    setAddressFromClient(client) {
      console.log('Setting address from client:', client);
      if (client && client.Address) {
        this.newAppointment.address = client.Address + ' ' + (client.ZIPCode || '') + ' ' + (client.City || '') + ' ' + (client.County || '');
        this.newAppointment.latitude = client.Latitude || null;
        this.newAppointment.longitude = client.Longitude || null;
      }
    },
    setAddressFromContact(contact) {
      console.log('Setting address from contact:', contact);
      if (contact) {
        // Usa l'indirizzo già formattato dal controller o costruiscilo
        this.newAppointment.address = contact.full_address || (contact.Address + ' ' + (contact.ZIPCode || '') + ' ' + (contact.City || '') + ' ' + (contact.County || ''));
        this.newAppointment.latitude = contact.Latitude || null;
        this.newAppointment.longitude = contact.Longitude || null;
        
        // Pulisce i campi cliente/sede dato che stiamo usando un contatto
        this.newAppointment.company = '';
        this.newAppointment.branch_id = '';
        this.sites = [];
      }
    },
    setAddressFromSite() {
      const site = this.sites.find(site => site.id === this.newAppointment.branch_id);
      if (site && site.Address) {
        this.newAppointment.address = site.Address + ' ' + (site.ZIPCode || '') + ' ' + (site.City || '') + ' ' + (site.County || '');
        this.newAppointment.latitude = site.Latitude || null;
        this.newAppointment.longitude = site.Longitude || null;
      }
    },
    getSites(clientId) {
      axios.get(route('modules.contratti.sedi', clientId))
        .then((response) => {
          this.sites = response.data.map(item => {
            const company = item.CompanyName ? item.CompanyName + " - " : "";
            const name = `${company}${item.Address} ${item.ZIPCode} ${item.City} ${item.County}`;
            return {
              id: item.id,
              name: name,
              Address: item.Address,
              ZIPCode: item.ZIPCode,
              City: item.City,
              County: item.County,
              Latitude: item.Latitude,
              Longitude: item.Longitude
            };
          });
          if (this.newAppointment.branch_id) {
            this.setAddressFromSite();
          }
        }
      );
    },
        openFileDialog() {
      this.$refs.input && this.$refs.input.click()
    },
    onInputChange(e) {
      const newFiles = e.target.files
      this.handleFiles(newFiles)
      e.target.value = ''
    },
    onDrop(e) {
      const dropped = e.dataTransfer.files
      this.onDragLeave()
      this.handleFiles(dropped)
    },
    onDragEnter() {},
    onDragOver() {},
    onDragLeave() {},
    handleFiles(fileList) {

        this.currentAttachments = [...this.currentAttachments, ...fileList]
    },
    removeFile(f) {
      this.currentAttachments = this.currentAttachments.filter(x => x !== f)
    },
    emitFiles() {
      let _this = this;
      let packets = [];

      _this.readfileramaining = _this.currentAttachments.length;

      for (const file of this.currentAttachments) {
        let reader = new FileReader();

        reader.addEventListener("loadend", function (evt) { 
          packets.push( { name: file.name , data: new Uint8Array(evt.currentTarget.result).toBase64()}); 
          _this.readfileramaining--;
          if (_this.readfileramaining == 0){
            _this.concludeAppointment(packets);
          }
        });
        reader.readAsArrayBuffer(file)
      }
    },
    prettySize(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let i = 0
      let size = bytes
      while (size >= 1024 && i < units.length - 1) {
        size = size / 1024
        i++
      }
      return `${Math.round(size * 10) / 10} ${units[i]}`
    },
    loadAttachments()
    {
      axios.post(route('modules.visite-commerciali.attachments'), { id: this.editingVisit.id})
        .then((response) => {
          this.attachmentLoaded = true;
          if (response.data.success){
            this.visitAttachments = response.data.attachments;        
          }
          else this.visitAttachments = [];
        }
      );
    }
  }
};
</script>